import { useState } from 'react';

import SettingsDialog from './SettingsDialog';
import {
  allowlistLabel,
  budgetState,
  formatCohortDate,
  segmentCostMissing,
} from './journeySummary';
import {
  budgetDraftFromGlobal,
  buildBudgetChanges,
  buildPilotChanges,
  buildTaxGuideChanges,
  pilotDraftFromGlobal,
  TAX_GUIDE_MAX_LENGTH,
  taxGuideDraftFromGlobal,
} from './settingsForm';
import { Caption, EditLink, Field, RowBody, RowLabel } from './styles';
import { formatMoney } from '../shared/format';

import useAuth from '@/hooks/useAuth';
import PermissionComponent from '@/routes/PermissionComponent';
import { JourneyChanges, LifecycleGlobal } from '@/services/lifecycleAPI';

type Props = {
  global: LifecycleGlobal;
  onSave: (changes: JourneyChanges) => Promise<void>;
};

const TenantOnly = ({
  testId,
  label,
  onClick,
}: {
  testId: string;
  label: string;
  onClick: () => void;
}) => {
  const { currentAdminIsTenantAdminRole } = useAuth();
  return (
    <span
      title={currentAdminIsTenantAdminRole ? undefined : 'Tenant admin only'}
    >
      <PermissionComponent id="btn-lifecycle-settings" showAsReadOnly>
        <EditLink
          aria-label={label}
          data-testid={testId}
          disabled={!currentAdminIsTenantAdminRole}
          type="button"
          onClick={onClick}
        >
          Edit
        </EditLink>
      </PermissionComponent>
    </span>
  );
};

export const PilotForm = ({
  global,
  onSave,
  onClose,
}: Props & { onClose: () => void }) => {
  const [initial] = useState(() => pilotDraftFromGlobal(global));
  const [draft, setDraft] = useState(initial);
  const set = (key: keyof typeof draft) => (e: { target: { value: string } }) =>
    setDraft((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <SettingsDialog
      build={() => buildPilotChanges(initial, draft)}
      testId="pilotModal"
      version={global.version}
      title="Edit pilot"
      onClose={onClose}
      onSave={onSave}
    >
      <Field>
        <label htmlFor="pilot-cohort">Cohort start date</label>
        <input
          id="pilot-cohort"
          type="date"
          value={draft.cohortStartOn}
          onChange={set('cohortStartOn')}
        />
        <Caption>
          Only workers who signed up on or after this date get journeys.
        </Caption>
      </Field>
      <Field>
        <label htmlFor="pilot-zips">Zip prefixes</label>
        <input
          id="pilot-zips"
          value={draft.zipPrefixes}
          onChange={set('zipPrefixes')}
        />
        <Caption>Comma separated. 3 to 5 digits each.</Caption>
      </Field>
      <Field>
        <label htmlFor="pilot-allow">Allowlist (worker ids)</label>
        <input
          id="pilot-allow"
          value={draft.allowlist}
          onChange={set('allowlist')}
        />
        {global.allowlist_enforced ? (
          <Caption>
            Only the workers on this list get messages. Empty = nobody gets
            messages.
          </Caption>
        ) : (
          <Caption data-testid="pilotAllowlistIgnored">
            This environment sends to everyone in the pilot zips; the allowlist
            is ignored.
          </Caption>
        )}
      </Field>
    </SettingsDialog>
  );
};

export const BudgetForm = ({
  global,
  onSave,
  onClose,
}: Props & { onClose: () => void }) => {
  const [initial] = useState(() => budgetDraftFromGlobal(global));
  const [draft, setDraft] = useState(initial);

  return (
    <SettingsDialog
      build={() => buildBudgetChanges(initial, draft)}
      size="xs"
      testId="budgetModal"
      version={global.version}
      title="Monthly SMS budget"
      onClose={onClose}
      onSave={onSave}
    >
      <Field>
        <label htmlFor="budget-dollars">Budget in dollars</label>
        <input
          id="budget-dollars"
          inputMode="decimal"
          value={draft.dollars}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, dollars: e.target.value }))
          }
        />
        <Caption>
          When the month's SMS spend reaches it, messages go by push or email
          instead (reason "SMS budget used up").
        </Caption>
      </Field>
      <Field>
        <label htmlFor="budget-segment-cost">SMS segment cost (cents)</label>
        <input
          id="budget-segment-cost"
          inputMode="decimal"
          value={draft.segmentCost}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, segmentCost: e.target.value }))
          }
        />
        <Caption>
          What one SMS segment costs us. Each text's spend is its segments times
          this; empty counts every text as $0.
        </Caption>
      </Field>
    </SettingsDialog>
  );
};

export const TaxGuideForm = ({
  global,
  onSave,
  onClose,
}: Props & { onClose: () => void }) => {
  const [initial] = useState(() => taxGuideDraftFromGlobal(global));
  const [draft, setDraft] = useState(initial);

  return (
    <SettingsDialog
      build={() => buildTaxGuideChanges(initial, draft)}
      size="sm"
      testId="taxGuideModal"
      version={global.version}
      title="Edit tax guide link"
      onClose={onClose}
      onSave={onSave}
    >
      <Field>
        <label htmlFor="tax-guide-url">Tax guide link</label>
        <input
          id="tax-guide-url"
          inputMode="url"
          maxLength={TAX_GUIDE_MAX_LENGTH}
          placeholder="https://"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Caption>
          The J2 tax messages link here. Must start with https://. Leave it
          empty to clear it, and J2 tax messages are skipped.
        </Caption>
      </Field>
    </SettingsDialog>
  );
};

const PilotBudgetRows = ({ global, onSave }: Props) => {
  const [open, setOpen] = useState<'pilot' | 'budget' | 'taxGuide' | null>(
    null,
  );
  const budget = budgetState(global);
  // "The budget never stops SMS" is true only while a budget is running: with
  // no budget, or one used up, SMS is already off and the row says so above
  // (bug hunt 18). MessagingControls flags it the same way.
  const costWarning = budget.kind === 'ok' && segmentCostMissing(global);
  const cost = global.sms_segment_cost_cents;
  // graphy prices a cost of 0 like a blank one (segmentCostMissing), so this
  // row says "not set" for both, as the Overview and Go live do (A5-04).
  const costText =
    cost === null || cost <= 0
      ? 'Segment cost not set'
      : `${cost}¢ per SMS segment`;
  const costLine = costWarning
    ? `${costText}: SMS spend is not counted, so the budget never stops SMS`
    : costText;
  const pct = budget.kind === 'ok' ? budget.pct : 100;
  const over = budget.kind === 'spent' || pct > 90;
  const close = () => setOpen(null);

  return (
    <>
      <RowLabel>Pilot</RowLabel>
      <RowBody css={{ gap: '6px 18px' }}>
        <span>
          Signed up on or after{' '}
          <strong>{formatCohortDate(global.cohort_start_on)}</strong>
        </span>
        <span>
          Zip prefixes{' '}
          <strong>{global.cohort_zip_prefixes || 'Not set'}</strong>
        </span>
        <span>{allowlistLabel(global)}</span>
        <TenantOnly
          label="Edit pilot"
          testId="pilotEditBtn"
          onClick={() => setOpen('pilot')}
        />
      </RowBody>

      <RowLabel>SMS budget</RowLabel>
      <RowBody css={{ gap: '8px 16px' }}>
        {budget.kind === 'none' ? (
          <span>SMS is off: no monthly budget is set.</span>
        ) : budget.kind === 'zero' ? (
          <span style={{ color: '#B0030A' }}>SMS is off: budget is $0.</span>
        ) : (
          <>
            <span>
              <strong>{formatMoney(budget.spend)}</strong> of{' '}
              <strong>{formatMoney(budget.budget)}</strong> this month
            </span>
            <div
              aria-label={`${pct}% of the budget used`}
              data-testid="budgetBar"
              role="img"
              style={{
                width: 220,
                height: 6,
                background: '#EDEDED',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                data-over={over}
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: over ? '#CF040C' : '#1E8E3E',
                }}
              />
            </div>
            {budget.kind === 'spent' ? (
              <Caption style={{ color: '#B0030A' }}>
                {budget.overBy > 0
                  ? `Over budget by ${formatMoney(budget.overBy)}: SMS is not sent`
                  : 'Used up: SMS is not sent'}
              </Caption>
            ) : (
              <Caption>{pct}% used</Caption>
            )}
          </>
        )}
        <Caption
          data-testid="segmentCost"
          style={costWarning ? { color: '#B0030A' } : undefined}
        >
          {costLine}
        </Caption>
        <TenantOnly
          label="Edit SMS budget"
          testId="budgetEditBtn"
          onClick={() => setOpen('budget')}
        />
      </RowBody>

      <RowLabel>Tax guide link</RowLabel>
      <RowBody css={{ gap: '8px 16px' }}>
        <span data-testid="taxGuideValue" style={{ minWidth: 0 }}>
          {global.tax_guide_url ? (
            <a
              href={global.tax_guide_url}
              rel="noopener noreferrer"
              style={{ overflowWrap: 'anywhere' }}
              target="_blank"
            >
              {global.tax_guide_url}
            </a>
          ) : (
            <span style={{ color: '#7A5600' }}>
              Not set: J2 tax messages are skipped
            </span>
          )}
        </span>
        <TenantOnly
          label="Edit tax guide link"
          testId="taxGuideEditBtn"
          onClick={() => setOpen('taxGuide')}
        />
      </RowBody>

      {open === 'pilot' && (
        <PilotForm global={global} onClose={close} onSave={onSave} />
      )}
      {open === 'budget' && (
        <BudgetForm global={global} onClose={close} onSave={onSave} />
      )}
      {open === 'taxGuide' && (
        <TaxGuideForm global={global} onClose={close} onSave={onSave} />
      )}
    </>
  );
};

export default PilotBudgetRows;
